import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { HYDRATE } from "next-redux-wrapper";
import { format, isValid } from "date-fns";
import { createWrapper } from "admin/api/axiosWrapper";
import { ErrorResponse } from "admin/types/errorResponse";
import {
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
} from "admin/utils/sliceUtil";
import { HotelOption } from "admin/types/hotelOption";
import { RootState } from "admin/store";
import {
  API_BASE_PATH,
  COMPANY_ADMIN_API_VER,
} from "admin/constants/constants";
import { PageContext } from "admin/types/pageContext";
import { AdminTypes } from "admin/constants/adminTypes";
import { objectToFormData } from "admin/utils/objectUtil";
import { parseDate } from "admin/utils/dateUtil";
import { HotelOptionApplicableConditionTypes } from "../../../constants/hotelOptionApplicableConditionTypes";

const key = "hotelOption";
const adapter = createEntityAdapter<HotelOption.Option>();

const initialStateValues: HotelOption.HotelOptionState = {
  hotelOption: null,
  errors: null,
  loading: false,
};

const initialState = adapter.getInitialState(initialStateValues);

/** Async **/
export const fetchHotelOptions = createAsyncThunk<
  {
    options: HotelOption.Option[];
  },
  {
    context: PageContext.PageContext;
    hotelId: string;
    param?: string;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchOptions`, async (args, { rejectWithValue }) => {
  try {
    const {
      context,
      hotelId,
      param,
      isServerSide = false,
      headers,
      locale,
    } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/options.json?${param}`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/options.json?${param}`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      options: result.data.hotelOptions,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const fetchHotelOption = createAsyncThunk<
  {
    option: HotelOption.Option;
  },
  {
    context: PageContext.PageContext;
    optionId: number;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchOption`, async (args, { rejectWithValue }) => {
  try {
    const { context, optionId, isServerSide = false, headers, locale } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotel_options/${optionId}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotel_options/${optionId}.json`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      option: result.data.hotelOption,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postHotelOption = createAsyncThunk<
  { option: HotelOption.Option },
  { context: PageContext.PageContext; hotelId: string; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postHotelOption`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/options.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/options.json`;
    const formData: any = new FormData();
    objectToFormData(params, formData, "hotel_option");
    const result = await createWrapper({ isMultipart: true }).post(
      url,
      formData,
    );
    return { option: result.data.hotelOption };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putHotelOption = createAsyncThunk<
  { option: HotelOption.Option },
  {
    context: PageContext.PageContext;
    id: number;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putHotelOption`, async (args, { rejectWithValue }) => {
  try {
    const { context, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotel_options/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotel_options/${id}.json`;
    const formData: any = new FormData();
    objectToFormData(params, formData, "hotel_option");
    const result = await createWrapper({ isMultipart: true }).put(
      url,
      formData,
    );
    return { option: result.data.hotelOption };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putHotelOptionOrder = createAsyncThunk<
  { option: HotelOption.Option },
  {
    context: PageContext.PageContext;
    id: number;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putHotelOptionOrder`, async (args, { rejectWithValue }) => {
  try {
    const { context, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotel_options/${id}/order.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotel_options/${id}/order.json`;
    const result = await createWrapper().put(url, { hotel_option: params });
    return { option: result.data.hotelOption };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const deleteHotelOptionImage = createAsyncThunk<
  { hotelOptionId?: number },
  { context: PageContext.PageContext; hotelOptionId?: number; id: number },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/deleteHotelOptionImage`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelOptionId, id } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotel_option_images/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotel_option_images/${id}.json`;
    await createWrapper().delete(url);
    return { hotelOptionId: hotelOptionId };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

/** slice **/
export const hotelOptionSlice = createSlice({
  name: key,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        return { ...state, ...action.payload.hotelOption };
      })
      .addCase(fetchHotelOptions.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.options);
      })
      .addCase(fetchHotelOption.fulfilled, (state, action) => {
        adapter.upsertOne(state, action.payload.option);
      })
      .addCase(postHotelOption.fulfilled, (state, action) => {
        adapter.upsertOne(state, action.payload.option);
      })
      .addCase(putHotelOption.fulfilled, (state, action) => {
        adapter.upsertOne(state, action.payload.option);
      })
      .addCase(putHotelOptionOrder.fulfilled, (state, action) => {
        adapter.upsertOne(state, action.payload.option);
      })
      .addCase(deleteHotelOptionImage.fulfilled, (state, action) => {
        const { hotelOptionId } = action.payload;
        if (state.entities[hotelOptionId]) {
          state.entities[hotelOptionId].image = null;
        }
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.errors = action.payload;
        state.loading = false;
      });
  },
});

/** selector **/
export const {
  selectById: hotelOptionByIdSelector,
  selectIds: hotelOptionIdsSelector,
  selectEntities: hotelOptionEntitiesSelector,
  selectAll: hotelOptionsSelector,
  selectTotal: totalHotelOptionsSelector,
} = adapter.getSelectors((state: RootState) => state.hotelOption);

const stateSelector = (state: RootState) => state[key];

export const errorsSelector = createSelector(
  stateSelector,
  (state) => state.errors,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

export const convertHotelOptionFormValuesToParams = (data: any) => {
  const params = { ...data };
  if (params.server_image) {
    delete params.server_image;
  } else if (!params.image) {
    params.image = null;
  } else {
    params.image = { value: params.image[0] };
  }

  // formDataがnullとundefinedに対応してないので、空文字を入れる
  if (!params.accommodatable_start_date) {
    params.accommodatable_start_date = "";
  }
  if (!params.accommodatable_end_date) {
    params.accommodatable_end_date = "";
  }
  if (!params.bookable_start_at) {
    params.bookable_start_at = "";
  }
  if (!params.bookable_end_at) {
    params.bookable_end_at = "";
  }

  if (params.applicable_condition === HotelOptionApplicableConditionTypes.all) {
    delete params.plan_ids;
    params.is_empty_plan_ids = true;
  }

  return params;
};

export const convertHotelOptionToFormValues = (data: any) => {
  const values = { ...data };
  if (values.image) {
    values.server_image = values.image;
    delete values.image;
  }
  if (values.planIds) {
    values.plan_ids = values.planIds.map((planId) => String(planId));
    delete values.planIds;
  }
  if (values.bookableStartAt) {
    const startAtDate = parseDate(values.bookableStartAt);
    if (isValid(startAtDate)) {
      values.bookableStartAt = format(startAtDate, "yyyy-MM-dd HH:mm");
    }
  }
  if (values.bookableEndAt) {
    const endAtDate = parseDate(values.bookableEndAt);
    values.bookableEndAt = format(endAtDate, "yyyy-MM-dd HH:mm");
  }
  return values;
};
